import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs-timezone-iana-plugin";
import "dayjs/locale/uk.js";
import {TIMEZONE} from "../constans";

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.locale("uk");

/** Календарна дата в часовому поясі застосунку (узгоджено з HeaderControls: Сьогодні, місяць тощо). */
export function formatRangeDateForApi(date) {
    return dayjs(date).tz(TIMEZONE).format("YYYY-MM-DD");
}

export function formatRangeDayMonthInAppTz(date) {
    return dayjs(date).tz(TIMEZONE).format("DD-MM");
}

/**
 * KeyCRM `filter[created_between]`: межі календарних днів у TIMEZONE (Київ),
 * у відповіді — UTC-рядки `YYYY-MM-DD HH:mm:ss`, як у live-summary та orderUpdater.
 * Без цього «наївні» 00:00–23:59 зміщують замовлення близько півночі (напр. 00:20).
 */
export function keyCrmCreatedBetweenUtcFromRange(startDate, endDate) {
    const startUtc = dayjs(startDate).tz(TIMEZONE).startOf("day").utc().format("YYYY-MM-DD HH:mm:ss");
    const endUtc = dayjs(endDate).tz(TIMEZONE).endOf("day").utc().format("YYYY-MM-DD HH:mm:ss");
    return { startUtc, endUtc };
}

export const compareOfferWithCell = (a, b) =>
    a.length === b.length && a.every((element, index) => element === b[index]);

export const sorStartEnd = (a, b) => {
    if (a > b) return -1;
    if (a < b) return 1;
    return 0;
};

export const sorEndStart = (a, b) => {
    if (a > b) return 1;
    if (a < b) return -1;
    return 0;
};

export const sorEndStartForProducts = (a, b) => {
    a = a[0].product.name;
    b = b[0].product.name;
    if (a > b) return 1;
    if (a < b) return -1;
    return 0;
};
export const sorEndStartForProductsW = (a, b) => {
    a = a[0].name;
    b = b[0].name;
    if (a > b) return 1;
    if (a < b) return -1;
    return 0;
};

export const getDates = (startDate, endDate, interval) => {
    const dates = [];
    let current = new Date(startDate);

    while (current <= endDate) {
        dates.push(dayjs(current).format("YYYY-MM-DD")); // ← Локально!
        current = new Date(current.getTime() + interval);
    }

    return dates;
};

export { normalizeProductName } from "../lib/productNameNormalize/runtime.js";

export const getDayMetric = (day, selectedSources) => {
    let total = 0;
    if ((selectedSources.includes(1) || selectedSources.includes(4)) || selectedSources.includes("instagram") && day.instagram) {
        total += day.instagram;
    }
    if (selectedSources.includes(2) || selectedSources.includes("telegram") && day.telegram) {
        total += day.telegram;
    }
    return total;
};


export const formatDateToKyiv = (date, format = "D MMM HH:mm") => {
    if (!date) return "";
    return dayjs(date).tz(TIMEZONE).format(format);
};

export function filterTransactionsByType(all, type) {
    if (type === "cash") {
        return all.filter(tx => tx.paymentType === "cash");
    }

    if (type === "business") {
        return all.filter(tx => tx.categoryType !== "personal");
    }

    if (type === "personal") {
        return all.filter(tx => tx.categoryType === "personal");
    }

    return [];
}

export const isUncategorized = (tx) =>
    !tx.category || tx.category === "";

export const isRealIncome = (tx) => {
    if (tx.direction !== "income") return false;
    if (tx.isInternal) return false;
    if (tx.excludeFromTotals) return false;
    if (tx.paymentType === "cash" && tx.isCashMirror) return false;
    return true;
};

export function calculateCashBalanceUpTo(transactions) {
    let balance = 0;

    for (const tx of transactions) {
        if (tx.paymentType !== "cash") continue;
        if (tx.excludeFromTotals) continue; // 🔥 зеркала не считаем

        if (tx.direction === "income") {
            balance += tx.amount;
        }

        if (tx.direction === "expense") {
            balance -= Math.abs(tx.amount);
        }
    }

    return balance / 100;
}

/**
 * Текст для пошуку по колонках таблиці: Тип (direction), Категорія (categoryType),
 * Підкатегорія (category), Карта (як у CardLabel).
 * @param {Record<string, string>} [labelBySlug] — з useFopLabels().labelBySlug
 */
export function buildTransactionTableSearchHaystack(tx, labelBySlug) {
    const parts = [];

    const dir = tx.direction || "";
    parts.push(dir);
    if (dir === "income") {
        parts.push("надходження", "дохід");
    } else if (dir === "expense") {
        parts.push("витрати", "розхід");
    }

    const ct = tx.categoryType || "";
    parts.push(ct);
    if (ct === "business") {
        parts.push("бізнес");
    } else if (ct === "personal") {
        parts.push("особисті", "особисте", "особиста");
    }

    if (tx.category) {
        parts.push(tx.category);
    } else {
        parts.push("без категорії");
    }

    const slug = tx.fop;
    const fullText =
        (slug && labelBySlug && labelBySlug[slug]) || slug || "";
    const tokens = fullText.trim().split(/\s+/);
    const fopShort = tokens.length === 4 ? tokens[1] : fullText;

    const cardTypeLabel =
        tx.cardType === "black"
            ? "Чорна карта"
            : tx.cardType === "white"
              ? "Біла карта"
              : tx.cardType === "fop"
                ? "monobank ФОП"
                : tx.cardType === "novapay_fop"
                  ? "NovaPay"
                  : tx.cardType === "cash"
                    ? "Cash"
                    : "Card";

    parts.push(fopShort, fullText, slug, cardTypeLabel);

    return parts.filter(Boolean).join(" ").toLowerCase();
}

/**
 * Розбір відповіді `GET /api/production/variants`: зазвичай масив оферів;
 * при `debug=1` — об'єкт `{ offers, _variantServerDebug }` (етапи пошуку в ProductW).
 */
export function parseProductionVariantSearchResponse(data) {
    if (data == null) {
        return { offers: [], serverDebug: null };
    }
    if (Array.isArray(data)) {
        return { offers: data, serverDebug: null };
    }
    return {
        offers: Array.isArray(data.offers) ? data.offers : [],
        serverDebug: data._variantServerDebug ?? null,
    };
}

